import { memo } from 'react';
import { ArrowRight, RefreshCw, Trash2 } from 'lucide-react';

/**
 * ThoughtCard — single parked-thought row used inside QuickCapture's
 * review tray. Pure presentational; receives the AI classification result
 * and the convert/dismiss/reclassify callbacks.
 *
 * Wrapped in `React.memo` so stable thoughts skip re-render when sibling
 * thoughts change (the parent re-renders on every poll tick). The
 * `arePropsEqual` comparator allows for deep equality on the few
 * primitives we actually depend on without comparing function refs that
 * are guaranteed stable by the parent's `useCallback`.
 *
 * The two-state UX (classified vs. unclassified) is preserved as-is.
 */
function ThoughtCard({
  thought,
  convertTargets,
  classification,
  converting,
  classifying,
  onConvert,
  onReclassify,
  onDismiss,
  renderClassificationBadge,
}) {
  const ai = classification;
  const hasAI = !!(ai && ai.type);
  const suggestedTarget = convertTargets.find((t) => t.key === ai?.type);
  const SuggestedIcon = suggestedTarget?.icon;
  // Precomputed outside JSX so the render body stays free of filter chains.
  const alternateTargets = hasAI ? convertTargets.filter((t) => t.key !== ai.type) : convertTargets;

  return (
    <div
      data-testid={`parking-lot-thought-${thought.id}`}
      className="rounded-xl p-3"
      style={{
        background: 'var(--pd-panel-2)',
        border: `1px solid ${hasAI ? `${suggestedTarget?.color}20` : 'var(--pd-border)'}`,
      }}
    >
      <div className="text-[12px] font-medium" style={{ color: 'var(--pd-text)' }}>
        {thought.text}
      </div>

      {hasAI ? (
        <>
          {renderClassificationBadge ? renderClassificationBadge(ai) : null}
          <div className="flex items-center gap-1.5 mt-2">
            <button
              data-testid={`convert-ai-${thought.id}`}
              onClick={() => onConvert(thought)}
              disabled={converting === thought.id}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold text-white transition-colors"
              style={{ background: suggestedTarget?.color || 'var(--pd-focus)' }}
            >
              {SuggestedIcon ? <SuggestedIcon className="w-3 h-3" /> : null}
              {ai.fields?.title !== thought.text
                ? `${suggestedTarget?.label}: "${(ai.fields?.title || '').slice(0, 25)}..."`
                : `Convert to ${suggestedTarget?.label}`}
              <ArrowRight className="w-3 h-3" />
            </button>
            {alternateTargets.map((target) => (
              <button
                key={target.key}
                data-testid={`convert-${thought.id}-${target.key}`}
                onClick={() => onConvert(thought, target.key)}
                disabled={converting === thought.id}
                className="flex items-center gap-0.5 px-1.5 py-1 rounded-lg text-[8px] font-semibold transition-colors"
                style={{ background: `${target.color}10`, color: target.color, border: `1px solid ${target.color}15` }}
              >
                <target.icon className="w-2.5 h-2.5" />
              </button>
            ))}
            <button
              data-testid={`dismiss-thought-${thought.id}`}
              onClick={() => onDismiss(thought.id)}
              className="ml-auto p-1 rounded-lg"
              style={{ color: 'var(--pd-subtle)' }}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        </>
      ) : (
        <div className="mt-2">
          <div className="flex items-center gap-2 mb-2">
            <div className="flex items-center gap-1 text-[9px]" style={{ color: 'var(--pd-subtle)' }}>
              <RefreshCw className={`w-2.5 h-2.5 ${classifying === thought.id ? 'animate-spin' : ''}`} />
              {classifying === thought.id ? 'Classifying...' : 'Unclassified'}
            </div>
            <button
              data-testid={`reclassify-${thought.id}`}
              onClick={() => onReclassify(thought.id)}
              disabled={classifying === thought.id}
              className="text-[9px] font-semibold px-1.5 py-0.5 rounded-lg"
              style={{ color: 'var(--pd-focus)', background: 'var(--pd-blue-soft)' }}
            >
              Retry AI
            </button>
          </div>
          <div className="flex items-center gap-1">
            {convertTargets.map((target) => (
              <button
                key={target.key}
                data-testid={`convert-${thought.id}-${target.key}`}
                onClick={() => onConvert(thought, target.key)}
                disabled={converting === thought.id}
                className="flex items-center gap-1 px-2 py-1 rounded-lg text-[9px] font-semibold transition-colors"
                style={{ background: `${target.color}12`, color: target.color, border: `1px solid ${target.color}20` }}
              >
                <target.icon className="w-2.5 h-2.5" />
                {target.label}
              </button>
            ))}
            <button
              data-testid={`dismiss-thought-${thought.id}`}
              onClick={() => onDismiss(thought.id)}
              className="ml-auto p-1 rounded-lg"
              style={{ color: 'var(--pd-subtle)' }}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Bail out of re-render when neither the thought nor the busy-state flags
// referencing THIS card change. Sibling re-renders (poll ticks landing
// updated classifications on OTHER cards) won't cascade.
function arePropsEqual(prev, next) {
  if (prev.thought !== next.thought) return false;
  if (prev.classification !== next.classification) return false;
  // converting / classifying are scalar IDs; only matters if they target this card.
  const myId = prev.thought.id;
  const prevBusy = prev.converting === myId || prev.classifying === myId;
  const nextBusy = next.converting === myId || next.classifying === myId;
  if (prevBusy !== nextBusy) return false;
  // Callbacks expected stable via useCallback in parent — skip ref check.
  return true;
}

export default memo(ThoughtCard, arePropsEqual);
